import debounce from 'lodash.debounce'
import React from 'react'

export const useDebouncedInput = (delay?: number) => {
  const [searchTerm, setSearchTerm] = React.useState('')

  const debouncedResults = React.useMemo(
    () =>
      debounce((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value)
      }, delay ?? 600),
    [delay]
  )

  React.useEffect(() => {
    return () => {
      debouncedResults.cancel()
    }
  })

  return { debouncedResults, searchTerm, setSearchTerm }
}

export const useDebouncedValue = <T>(inputValue: T, delay?: number) => {
  const [value, setValue] = React.useState<T>()
  const [isDebouncing, setIsDebouncing] = React.useState(false)

  const memoizedDelay = React.useMemo(() => delay ?? 600, [delay])
  const debouncedSetter = React.useMemo(() => {
    return debounce((v) => {
      setIsDebouncing(false)
      setValue(v)
    }, memoizedDelay)
  }, [memoizedDelay])

  React.useEffect(() => {
    setIsDebouncing(true)
    return debouncedSetter(inputValue)
  }, [debouncedSetter, inputValue])

  return { value, isDebouncing }
}

/**
 * A helper function to memoize an object based on its values - if all of its values
 * are unchanged, we memoize the object.
 * @param obj the object to memoize
 * @returns the memoized object
 */
export function useMemoizedObject<T extends Record<string, unknown>>(
  obj: T
): T {
  const memoizedObject = React.useMemo(() => {
    return { ...obj }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, Object.values(obj)) // or use Object.values(obj) for shallow objects

  return memoizedObject
}
